/**
 * DataGridStep.jsx
 * 체험형 레슨 스텝: 코드 수정 → 데이터 테이블 변화
 *
 * Refactored to use Pyodide (via Universal Runner) for real Python execution.
 * - Injects `mockData` as `df` DataFrame.
 * - Executes user code.
 * - Renders the resulting `df` in the grid.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import MonacoCodeEditor from '@components/editor/MonacoCodeEditor';
import ControlBar from '../ControlBar';
import { motion, AnimatePresence } from 'framer-motion';
import { executeCode, isRunnerReady } from '../../../services/runner';

function DataGridStep({ step, onBack, onNext, canGoBack }) {
  const [code, setCode] = useState(step.initialCode || '# 코드를 수정해보세요');
  const language = step.language || 'python';
  const mockData = step.mockData || [];

  // State for execution results — initialize columns from mockData so grid is visible before Pyodide loads
  const [gridData, setGridData] = useState(mockData);
  const [displayColumns, setDisplayColumns] = useState(() =>
    mockData.length > 0 ? Object.keys(mockData[0]) : []
  );
  const [gridError, setGridError] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isReady, setIsReady] = useState(false);

  // Debounce timer
  const debounceTimer = useRef(null);

  // Check if runner is ready (Pyodide loading)
  useEffect(() => {
    const checkReady = async () => {
      // Trigger a simple run to ensure Pyodide loads
      if (!isRunnerReady(language)) {
        setIsRunning(true);
        await executeCode(language, "print('init')");
        setIsRunning(false);
      }
      setIsReady(true);
    };
    checkReady();
  }, [language]);

  // Execute Code Function
  const runDataGridCode = useCallback(async (sourceCode) => {
    setIsRunning(true);
    setGridError(null);

    try {
      // 1. Prepare Context
      // If user code already imports pandas and creates df, run as-is.
      // Otherwise, prepend setup code to inject mockData as df.
      const hasOwnSetup = /import\s+pandas/.test(sourceCode) && /pd\.DataFrame/.test(sourceCode);

      let fullCode;
      if (hasOwnSetup) {
        // User code is self-contained — run directly
        fullCode = sourceCode;
      } else {
        // Inject mockData as df for simple query/transform steps
        const setupCode = `
import pandas as pd
import numpy as np
import json

# JS compatibility aliases for direct JSON injection
true = True
false = False
null = None

data = ${JSON.stringify(mockData)}
df = pd.DataFrame(data)
`;
        fullCode = `${setupCode}\n${sourceCode}`;
      }

      // 2. Execute
      const { variables, error } = await executeCode(language, fullCode);

      if (error) {
        setGridError(error);
        setIsRunning(false);
        return;
      }

      // 3. Extract 'df' variable
      const dfVar = variables['df'];

      if (dfVar && dfVar.type === 'DataFrame') {
        const { columns, data } = dfVar.value;
        setDisplayColumns(columns);
        setGridData(data);
      } else {
        // Fallback if df is lost or not a dataframe
        setGridError("Error: 'df' variable not found or is not a DataFrame.");
      }

    } catch (e) {
      setGridError(String(e));
    } finally {
      setIsRunning(false);
    }
  }, [language, mockData]);

  // Initial Run & Debounced Update
  useEffect(() => {
    if (!isReady) return;

    // Immediate run on mount/step change
    runDataGridCode(code);

    // Setup debounce for subsequent edits
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [isReady, step]); // Run when step changes or runner becomes ready

  const handleCodeChange = (newCode) => {
    setCode(newCode);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      runDataGridCode(newCode);
    }, 800); // 800ms debounce
  };

  // Helper to format cell value — must always return a string (never an object)
  const formatCellValue = (val) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'number') {
      if (Number.isInteger(val)) return val.toString();
      return val.toFixed(1).replace(/\.0$/, '');
    }
    if (typeof val === 'boolean') return val.toString();
    if (typeof val === 'object') {
      try { return JSON.stringify(val); } catch { return '[object]'; }
    }
    return String(val);
  };

  return (
    <div className="relative h-full flex flex-col">
      {/* Header */}
      <div className="px-6 pt-5 pb-4 border-b border-slate-800/50">
        <div className="flex items-center gap-2 mb-2">
          <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 text-[10px] font-bold rounded uppercase tracking-wider">
            Data Grid (Pyodide Engine)
          </span>
        </div>
        <h3 className="text-lg font-bold text-white mb-1">{step.title || '데이터 그리드'}</h3>
        {step.text && <p className="text-sm text-slate-400 leading-relaxed">{step.text}</p>}
      </div>

      {/* Split View: Editor + Grid */}
      <div className="flex-1 flex min-h-0">
        {/* Left: Code Editor */}
        <div className="w-1/2 flex flex-col border-r border-slate-800 min-h-0">
          <div className="flex-1 min-h-0 relative">
            <MonacoCodeEditor
              initialCode={step.initialCode || ''}
              language={language}
              onChange={handleCodeChange}
              height="100%"
              className="h-full flex flex-col"
            />
            {/* Loading Overlay for Editor */}
            {!isReady && (
              <div className="absolute inset-0 bg-black/50 z-10 flex items-center justify-center backdrop-blur-sm">
                <div className="text-cyan-400 text-xs font-mono animate-pulse">Initializing Python Engine...</div>
              </div>
            )}
          </div>
        </div>

        {/* Right: Data Grid */}
        <div className="w-1/2 bg-[#1e1e1e] flex flex-col min-h-0 relative">
          <div className="flex justify-between items-center px-4 py-2 border-b border-slate-800 text-xs font-mono text-slate-500">
            <div className="flex items-center gap-2">
              <i className="fas fa-table text-[10px]" /> LIVE PREVIEW
              {isRunning && <span className="text-yellow-400 ml-2 animate-pulse">Running...</span>}
              {!isRunning && !gridError && (
                <span className="text-slate-500 ml-2">rows: {gridData.length}</span>
              )}
            </div>
            {gridError && (
              <span className="text-red-400 flex items-center gap-1">
                <i className="fas fa-triangle-exclamation text-[10px]" /> Error
              </span>
            )}
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto p-4 relative">
            <div className={`bg-[#252526] rounded border border-slate-700 overflow-hidden transition-opacity duration-200 ${isRunning ? 'opacity-50' : 'opacity-100'}`}>
              {displayColumns.length > 0 ? (
                <table className="w-full text-left text-sm">
                  <thead className="bg-[#333] text-slate-300">
                    <tr>
                      {displayColumns.map(col => (
                        <th key={col} className="p-3 border-b border-slate-600">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {gridData.map((row, i) => (
                      <motion.tr
                        key={i} // Row index as key since data might change completely
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-slate-400 hover:bg-white/5 transition-colors"
                      >
                        {displayColumns.map(col => (
                          <td key={col} className={`p-3 font-mono ${typeof row[col] === 'number' ? 'text-cyan-400' : ''}`}>
                            {formatCellValue(row[col])}
                          </td>
                        ))}
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="flex items-center justify-center h-32 text-slate-500 text-sm italic">
                  {gridError ? '데이터를 표시할 수 없습니다.' : '표시할 데이터가 없습니다.'}
                </div>
              )}
            </div>

            {/* Loading Spinner Overlay */}
            {isRunning && (
               <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                 <div className="w-8 h-8 border-2 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin"></div>
               </div>
            )}
          </div>

          {/* Error Traceback */}
          <AnimatePresence>
            {gridError && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 20, opacity: 0 }}
                className="absolute bottom-16 left-4 right-4 bg-black/95 p-4 rounded border-l-4 border-red-500 text-xs font-mono shadow-2xl backdrop-blur-md max-h-[200px] overflow-auto"
              >
                <div className="text-red-400 font-bold mb-1">Execution Error:</div>
                <div className="text-slate-300 whitespace-pre-wrap">{gridError}</div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <ControlBar onBack={onBack} onNext={onNext} canGoBack={canGoBack} />
    </div>
  );
}

export default DataGridStep;
